import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ResourceRequest = () => {
  const [selectedResources, setSelectedResources] = useState([]);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    title: ''
  });

  const resources = [
    {
      id: 'digital-transformation-guide',
      title: 'Digital Transformation Playbook',
      description: 'A comprehensive guide to planning and executing successful digital transformation initiatives',
      type: 'Whitepaper',
      pages: '45 pages',
      downloadCount: '12,500+',
      icon: 'BookOpen',
      category: 'Strategy'
    },
    {
      id: 'roi-calculator',
      title: 'ROI Assessment Calculator',
      description: 'Interactive tool to calculate potential returns on transformation investments',
      type: 'Tool',
      pages: 'Interactive',
      downloadCount: '8,200+',
      icon: 'Calculator',
      category: 'Assessment'
    },
    {
      id: 'change-management-framework',
      title: 'Change Management Framework',
      description: 'Proven methodologies for managing organizational change during transformation',
      type: 'Guide',
      pages: '32 pages',
      downloadCount: '15,800+',
      icon: 'Users',
      category: 'Change Management'
    },
    {
      id: 'technology-readiness-assessment',
      title: 'Technology Readiness Assessment',
      description: 'Evaluate your organization\'s readiness for technology adoption and integration',
      type: 'Assessment',
      pages: '15 questions',
      downloadCount: '9,600+',
      icon: 'CheckSquare',
      category: 'Assessment'
    },
    {
      id: 'industry-benchmarks-report',
      title: 'Industry Benchmarks Report 2024',
      description: 'Latest industry performance metrics and transformation success indicators',
      type: 'Report',
      pages: '68 pages',
      downloadCount: '22,100+',
      icon: 'BarChart3',
      category: 'Research'
    },
    {
      id: 'process-optimization-toolkit',
      title: 'Process Optimization Toolkit',
      description: 'Templates and tools for identifying and improving operational processes',
      type: 'Toolkit',
      pages: '25 templates',
      downloadCount: '11,400+',
      icon: 'Settings',
      category: 'Operations'
    },
    {
      id: 'leadership-transformation-guide',
      title: 'Leadership in Transformation',
      description: 'How leaders can drive successful organizational transformation initiatives',
      type: 'Whitepaper',
      pages: '38 pages',
      downloadCount: '14,700+',
      icon: 'Crown',
      category: 'Leadership'
    },
    {
      id: 'cybersecurity-checklist',
      title: 'Cybersecurity Transformation Checklist',
      description: 'Essential security considerations during digital transformation projects',
      type: 'Checklist',
      pages: '12 pages',
      downloadCount: '18,900+',
      icon: 'Shield',
      category: 'Security'
    }
  ];

  const categories = ['All', 'Strategy', 'Assessment', 'Change Management', 'Research', 'Operations', 'Leadership', 'Security'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredResources = selectedCategory === 'All'
    ? resources
    : resources.filter(resource => resource.category === selectedCategory);

  const handleResourceToggle = (resourceId) => {
    setSelectedResources(prev =>
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedResources.length === filteredResources.length) {
      setSelectedResources([]);
    } else {
      setSelectedResources(filteredResources.map(r => r.id));
    }
  };

  const handleSubmit = () => {
    console.log('Resource request submitted:', { selectedResources, contactInfo });
  };

  const updateContactInfo = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-navy/30 to-primary/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4 font-heading">
            Resource Library Access
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Access our comprehensive collection of whitepapers, tools, and assessments to accelerate your transformation journey.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Resource Selection */}
          <div className="lg:col-span-2">
            {/* Category Filter */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-brand-cyan text-white' : 'glass-morph bg-card/60 text-text-primary hover:bg-brand-cyan/20 hover:text-brand-cyan border border-border'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Select All */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-text-primary">
                Available Resources ({filteredResources.length})
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedResources.length === filteredResources.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {/* Resource Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className={`glass-morph p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedResources.includes(resource.id)
                      ? 'border-brand-cyan bg-brand-cyan/20 backdrop-blur-sm' : 'bg-card/60 border-border hover:border-brand-cyan/50'
                  }`}
                  onClick={() => handleResourceToggle(resource.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-brand-cyan/20 rounded-lg flex items-center justify-center">
                      <Icon name={resource.icon} size={24} className="text-brand-cyan" />
                    </div>
                    <Checkbox
                      checked={selectedResources.includes(resource.id)}
                      onChange={() => handleResourceToggle(resource.id)}
                    />
                  </div>

                  <div className="mb-3">
                    <span className="inline-block px-2 py-1 bg-brand-orange/20 text-brand-orange text-xs font-medium rounded-full mb-2">
                      {resource.type}
                    </span>
                    <h4 className="font-semibold text-text-primary mb-2">{resource.title}</h4>
                    <p className="text-sm text-text-secondary">{resource.description}</p>
                  </div>

                  <div className="flex items-center justify-between text-xs text-text-secondary">
                    <span>{resource.pages}</span>
                    <div className="flex items-center">
                      <Icon name="Download" size={14} className="mr-1" />
                      <span>{resource.downloadCount}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-1">
            <div className="glass-morph bg-card/80 backdrop-blur-md border border-border rounded-xl p-6 shadow-sm sticky top-8">
              <h3 className="text-xl font-semibold text-text-primary mb-6">
                Request Access
              </h3>

              <div className="space-y-4 mb-6">
                <Input
                  label="Full Name"
                  type="text"
                  value={contactInfo.name}
                  onChange={(e) => updateContactInfo('name', e.target.value)}
                  required
                  className="border border-border rounded-md px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                />

                <Input
                  label="Email Address"
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => updateContactInfo('email', e.target.value)}
                  required
                  className="border border-border rounded-md px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                />

                <Input
                  label="Company Name"
                  type="text"
                  value={contactInfo.company}
                  onChange={(e) => updateContactInfo('company', e.target.value)}
                  required
                  className="border border-border rounded-md px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                />

                <Input
                  label="Job Title"
                  type="text"
                  value={contactInfo.title}
                  onChange={(e) => updateContactInfo('title', e.target.value)}
                  required
                  className="border border-border rounded-md px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
                />
              </div>

              {/* Selected Resources Summary */}
              <div className="mb-6">
                <h4 className="font-semibold text-text-primary mb-3">
                  Selected Resources ({selectedResources.length})
                </h4>
                {selectedResources.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="space-y-2">
                      {selectedResources.map((resourceId) => {
                        const resource = resources.find(r => r.id === resourceId);
                        return (
                          <li key={resourceId} className="flex items-center text-sm">
                            <Icon name="Check" size={14} className="text-success mr-2 flex-shrink-0" />
                            <span className="text-text-secondary truncate">{resource?.title}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary">No resources selected</p>
                )}
              </div>

              <Button
                variant="default"
                fullWidth
                onClick={handleSubmit}
                disabled={selectedResources.length === 0}
                className="mb-4"
              >
                <Icon name="Download" size={20} className="mr-2" />
                Request Selected Resources
              </Button>

              <div className="text-center">
                <p className="text-xs text-text-secondary">
                  Resources will be sent to your email within 5 minutes
                </p>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-center space-x-4 text-xs text-text-secondary">
                  <div className="flex items-center">
                    <Icon name="Shield" size={14} className="mr-1" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Lock" size={14} className="mr-1" />
                    <span>No Spam</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Eye" size={14} className="mr-1" />
                    <span>Privacy Protected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourceRequest;